import { Box, Button } from '@mui/material'
import { openNode, openNodeOnTKatBuilder } from '../../utility/TreeNavigation'
import React, { FC } from 'react'
import { ConfirmCommitButton } from 'shared/ui'
import { NodeType } from 'shared/types/ingestion'
import { idsForNode } from '../nodeUpdates/utility'
import { useDeleteNode } from '../TreeActions'
import { NodeTypeSelect } from '../utility/NodeTypeSelect'
import { retrieveIds, updateTreeNode } from '../TreeRequests'
import { NodeCreation } from '../utility/NodeCreation'
import { PotentiallyHtml } from '../../PotentiallyHtml'
import { SkillsView } from '../utility/SkillsView'
import { countChildren } from './utility'
import { useAtom, useAtomValue } from 'infrastructure/state/jotai.wrapper'
import { selectedNodeAtom, workingTreeAtom, workingTreeIdMap } from './atoms'
import { CopyNode } from './CopyNode'
import { MoveNode } from './MoveNode'
import { NodeEditLang } from '../LanguageAttribute/NodeEditLang'

// These are the available actions the user can perform on the selected node
export function useActionOptions(): [string, FC][] {
  const selectedNode = useAtomValue(selectedNodeAtom)
  if (!selectedNode) return []

  const options: [string, FC][] = [
    ['Open in Kiddom', OpenKiddom],
    ['Open in Tkat', OpenTKat],
    ['Edit Type', EditType],
    ['Edit Lang', EditLang],
    ['Delete Node', DeleteNode],
    ['Add Child Node', AddChild],
    ['Duplicate Node (within this Tree or a different Tree)', CopyNode],
    ['Move Node within this Tree', MoveNode],
  ]
  const hasSkills = !!selectedNode.nodes.find(
    (child) => child.type === NodeType.SkillsList,
  )
  if (hasSkills) {
    options.push(['Edit Skills', SkillsLists])
  }
  return options
}

function OpenKiddom() {
  const selectedNode = useAtomValue(selectedNodeAtom)
  const workingIdMap = useAtomValue(workingTreeIdMap)
  if (!selectedNode) return null
  return (
    <Button onClick={() => openNode(selectedNode, workingIdMap)}>
      Open Kiddom
    </Button>
  )
}

function OpenTKat() {
  const selectedNode = useAtomValue(selectedNodeAtom)
  if (!selectedNode) return null
  return (
    <Button onClick={() => openNodeOnTKatBuilder(selectedNode)}>
      Open TKat
    </Button>
  )
}

function EditType() {
  const { resetTree } = useAtomValue(workingTreeAtom)
  const selectedNode = useAtomValue(selectedNodeAtom)
  if (!selectedNode) return null

  async function updateType(type: string) {
    if (!selectedNode || type === selectedNode.type) return
    await updateTreeNode({
      ...retrieveIds(selectedNode),
      type,
    })
    resetTree?.()
  }

  return <NodeTypeSelect type={selectedNode.type} onSelect={updateType} />
}

function EditLang() {
  const selectedNode = useAtomValue(selectedNodeAtom)
  if (!selectedNode) return null
  return (
    <NodeEditLang
      nodeId={selectedNode.id}
      treeId={selectedNode.tree_id}
      versionId={selectedNode.version}
    />
  )
}

function DeleteNode() {
  const { resetTree } = useAtomValue(workingTreeAtom)
  const [selectedNode, setSelectedNode] = useAtom(selectedNodeAtom)
  const deleteNode = useDeleteNode()

  if (!selectedNode) return null
  async function doDelete() {
    if (!selectedNode) return
    await deleteNode(idsForNode(selectedNode))
    setSelectedNode(undefined)
    resetTree?.()
  }

  const counts = countChildren(selectedNode)

  return (
    <Box>
      <Box>{`Sub Nodes: ${counts.nodes} Sub Leaves: ${counts.leaves}`}</Box>
      <ConfirmCommitButton
        disabled={selectedNode.type === NodeType.Course}
        onClick={doDelete}
      >
        Delete
      </ConfirmCommitButton>
    </Box>
  )
}

function AddChild() {
  const { resetTree } = useAtomValue(workingTreeAtom)
  const selectedNode = useAtomValue(selectedNodeAtom)
  if (!selectedNode) return null
  return (
    <Box padding="5px" border={'1px solid'}>
      <NodeCreation node={selectedNode} onCreate={resetTree} />
    </Box>
  )
}

function SkillsLists() {
  const { resetTree } = useAtomValue(workingTreeAtom)
  const selectedNode = useAtomValue(selectedNodeAtom)
  if (!selectedNode) return null
  const skillsLists = selectedNode.nodes.filter(
    (child) => child.type === NodeType.SkillsList,
  )
  return (
    <Box>
      <Box>
        <PotentiallyHtml s={selectedNode.title} />
      </Box>
      <Box paddingTop={'5px'}>
        {skillsLists.map((node) => (
          <SkillsView key={node.id} node={node} onUpdate={resetTree} />
        ))}
      </Box>
    </Box>
  )
}
